import { digObject } from '@domain-group/javascript-utils'

import type { CampaignModel } from '@models/campaign'

import OrderItemTasks, {
  configOptions as orderItemTaskConfigOptions,
  icon as orderItemTasksIcon,
} from '@components/CustomizableDashboardModules/OrderItemTasks'

import Orders, {
  configOptions as orderConfigOptions,
  icon as ordersIcon,
} from '@components/CustomizableDashboardModules/Orders'

import Conversations, { icon as conversationsIcon } from '@components/CustomizableDashboardModules/Conversations'

import Quotes, {
  configOptions as quoteConfigOptions,
  icon as quotesIcon,
} from '@components/CustomizableDashboardModules/CampaignQuotes'

import Activities, {
  configOptions as activitiesConfigOptions,
  icon as ActivityIcon,
} from '../components/DashboardModules/Activities'

import Images, {
  configOptions as imageConfigOptions,
  icon as imagesIcon,
} from '../components/DashboardModules/Images'

export const generateAvailableModules = (
  campaign: CampaignModel,
) => {
  const { id } = campaign

  const customisableDashboardModules = {
    Activities: {
      component: Activities,
      configOptions: activitiesConfigOptions(),
      icon: ActivityIcon,
      id: 'Activities',
      isSelectable: true,
      title: 'Activities',
    },
    Conversations: {
      component: Conversations,
      configOptions: {},
      icon: conversationsIcon,
      id: 'Conversations',
      mergeConfig: {
        queryParams: { campaign_id: id },
      },
      isSelectable: true,
      title: 'Conversations',
    },
    Images: {
      component: Images,
      configOptions: imageConfigOptions(),
      icon: imagesIcon,
      id: 'Images',
      isSelectable: true,
      title: 'Images',
    },
    Orders: {
      component: Orders,
      configOptions: orderConfigOptions({ isCampaignManager: true }),
      icon: ordersIcon,
      id: 'Orders',
      mergeConfig: {
        moduleProps: { isCampaignManager: true },
        queryParams: { campaign_id: id },
      },
      isSelectable: true,
      title: 'Orders',
    },
    OrderItemTasks: {
      component: OrderItemTasks,
      configOptions: orderItemTaskConfigOptions(),
      icon: orderItemTasksIcon,
      id: 'OrderItemTasks',
      mergeConfig: {
        moduleProps: { isCampaignManager: true },
        queryParams: { campaign_id: id },
      },
      isSelectable: true,
      title: 'Order Tasks',
    },
    Quotes: {
      component: Quotes,
      configOptions: quoteConfigOptions({ isCampaignManager: true }),
      icon: quotesIcon,
      id: 'Quotes',
      mergeConfig: {
        moduleProps: { isCampaignManager: true },
        queryParams: { campaign_id: id },
      },
      isSelectable: true,
      title: 'Quotes',
    },
  }

  // @ts-expect-error TS2344
  const filteredModules: ReturnType<typeof customisableDashboardModules> = {}

  Object.keys(customisableDashboardModules).forEach((module) => {
    // @ts-expect-error TS7053
    const targetModule = customisableDashboardModules[module]

    if (targetModule.isSelectable){
      filteredModules[module] = targetModule
    }
  })

  return filteredModules
}

type DefaultModule = {
  key: string,
  moduleId: string,
  isAllowed?: boolean,
}

export const dashboardDefaultLayout = () => {
  const defaultLayout = {
    columns: {
      main: {
        id: 'main',
        modules: [
          {
            key: 'Quotes-1',
            moduleId: 'Quotes',
            isAllowed: true,
          },
        ],
      },
      sidebar: {
        id: 'sidebar',
        modules: [
          {
            key: 'Activity-1',
            moduleId: 'Activities',
            isAllowed: true,
          },
          {
            key: 'Images-1',
            moduleId: 'Images',
            isAllowed: true,
          },
          {
            key: 'OrderItemTasks-1',
            moduleId: 'OrderItemTasks',
            isAllowed: true,
          },
        ],
      },
    },
    custom: false,
    options: {
      'Activity-1': {
        customization: { title: 'Activity' },
        queryParams: {
          limit: 10,
        },
      },
      'Images-1': {
        customization: { title: 'Images' },
        queryParams: {
          limit: 5,
        },
      },
      'OrderItemTasks-1': {
        customization: { title: 'Order Tasks' },
        queryParams: {
          limit: 10,
        },
      },
      'Quotes-1': {
        customization: { display_mode: 'compact-list', title: 'Quotes' },
        queryParams: {
          authorization_sent: 'all',
          authorized: 'all',
          limit: 5,
          priority_quotes_by_user_id: 'all',
          status_key: 'all',
        },
      },
      'Orders-1': {
        customization: { display_mode: 'list', title: 'Orders' },
        queryParams: {
          campaign_marketing_type: 'all',
          limit: 5,
          order_type: 'all',
          status_group_key: 'all',
        },
      },
    },
  }

  const filteredDefaultLayout = JSON.parse(JSON.stringify(defaultLayout))

  const defaultModulesMain: DefaultModule[] = digObject(defaultLayout, 'columns.main.modules', {})
  defaultModulesMain.forEach((module, index) => {
    if (!module.isAllowed){
      filteredDefaultLayout.columns.main.modules.splice(index, 1)
    }
  })

  const defaultModulesSideBar: DefaultModule[] = digObject(filteredDefaultLayout, 'columns.sidebar.modules', {})
  defaultModulesSideBar.forEach((module, index) => {
    if (!module.isAllowed){
      filteredDefaultLayout.columns.sidebar.modules.splice(index, 1)
    }
  })
  return filteredDefaultLayout
}
